import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../redux/store";
import { useNavigate } from "react-router-dom";
import { deletePost, fetchPosts, upsertPost } from "../../../../redux/blogSlice";
import { motion, AnimatePresence } from "framer-motion";
import {
  Pencil, Copy, Trash2, ExternalLink, Link,
  AlertCircle, CheckCircle, X, RefreshCw, Search,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME
───────────────────────────────────────────────────────── */
const C = {
  paper:        "#ffffff",
  surface:      "#f7f7f7",
  ink:          "#0a0a0a",
  inkSoft:      "#2a2a2a",
  inkMid:       "#6b6b6b",
  inkLight:     "#a3a3a3",
  border:       "#e0e0e0",
  success:      "#1a7a3a",
  successBg:    "#f0faf4",
  successBorder:"#b8dfc8",
  danger:       "#b22222",
  dangerBg:     "#fff5f5",
  dangerBorder: "#f0c8c8",
  serif:        "'Cormorant Garamond', Georgia, serif",
  sans:         "'Montserrat', sans-serif",
};

const AllPosts: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const posts    = useSelector((state: RootState) => state.blog.posts);

  const [search,      setSearch]      = useState("");
  const [deleteId,    setDeleteId]    = useState<string | null>(null);
  const [copiedId,    setCopiedId]    = useState<string | null>(null);
  const [dupId,       setDupId]       = useState<string | null>(null);
  const [toast,       setToast]       = useState<{ type:"success"|"danger"; text:string } | null>(null);
  const [searchFocus, setSearchFocus] = useState(false);

  useEffect(() => { dispatch(fetchPosts()); }, [dispatch]);

  const flash = (type:"success"|"danger", text:string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── Original handlers (all preserved) ── */
  const handleDelete = async (id: string) => {
    await dispatch(deletePost(id));
    setDeleteId(null);
    flash("danger", "Post moved to trash.");
  };

  const handleDuplicate = async (post: any) => {
    setDupId(post.id);
    const newPost = {
      ...post,
      id:    undefined,
      title: post.title + " (Copy)",
      slug:  post.slug + "-copy",
    };
    await dispatch(upsertPost(newPost));
    await dispatch(fetchPosts());
    setDupId(null);
    flash("success", `"${post.title}" duplicated.`);
  };

  const handleCopy = (slug: string, id: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/${slug}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2200);
  };

  /* ── Filtered posts ── */
  const filtered = search.trim()
    ? posts.filter(p =>
        p.title?.toLowerCase().includes(search.toLowerCase()) ||
        p.category?.toLowerCase().includes(search.toLowerCase())
      )
    : posts;

  return (
    <div style={{ background:C.paper, fontFamily:C.sans, color:C.ink }}>
      <style>{FONT_CSS + AP_CSS}</style>

      {/* ── TITLE STRIP ── */}
      <div style={S.titleStrip}>
        <div style={{ height:2, background:C.ink, marginBottom:16 }}/>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          <div>
            <p style={S.eye}>CONTENT MANAGEMENT</p>
            <h1 style={S.title}>All Posts</h1>
          </div>
          <span style={S.countPill}>{posts.length} post{posts.length !== 1 ? "s" : ""}</span>
        </div>
        <div style={{ height:1, background:C.border }}/>
      </div>

      <div style={S.body}>

        {/* ── TOAST ── */}
        <AnimatePresence>
          {toast && (
            <motion.div key="toast"
              initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }}
              exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"11px 16px", marginBottom:20,
                background: toast.type==="success" ? C.successBg : C.dangerBg,
                border: `1px solid ${toast.type==="success" ? C.successBorder : C.dangerBorder}`,
              }}
            >
              {toast.type==="success"
                ? <CheckCircle size={13} color={C.success}/>
                : <AlertCircle size={13} color={C.danger}/>
              }
              <span style={{ fontSize:11, fontWeight:500, flex:1,
                color:toast.type==="success" ? C.success : C.danger }}>
                {toast.text}
              </span>
              <button onClick={() => setToast(null)}
                style={{ background:"none", border:"none", cursor:"pointer", color:C.inkMid }}>
                <X size={11}/>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── TOOLBAR: search + refresh ── */}
        <div style={S.toolbar}>
          <div style={{
            ...S.searchWrap,
            border: `1px solid ${searchFocus ? C.ink : C.border}`,
          }}>
            <Search size={13} color={searchFocus ? C.ink : C.inkLight} strokeWidth={1.5}/>
            <input
              type="text"
              placeholder="Search posts…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={() => setSearchFocus(true)}
              onBlur={() => setSearchFocus(false)}
              style={S.searchInput}
              className="ap-input"
            />
            {search && (
              <button onClick={() => setSearch("")}
                style={{ background:"none", border:"none", cursor:"pointer", color:C.inkMid, display:"flex" }}>
                <X size={11}/>
              </button>
            )}
          </div>

          <button onClick={() => dispatch(fetchPosts())} style={S.refreshBtn} title="Refresh">
            <RefreshCw size={13} strokeWidth={1.5}/>
          </button>
        </div>

        {/* ── EMPTY STATE ── */}
        {filtered.length === 0 && (
          <div style={S.empty}>
            <div style={{ fontSize:32, color:C.border, marginBottom:12 }}>∅</div>
            <p style={{ fontFamily:C.serif, fontSize:22, fontWeight:300, color:C.ink }}>
              {search ? "No posts found" : "No posts yet"}
            </p>
            <p style={{ fontSize:11, color:C.inkLight, marginTop:6 }}>
              {search ? `No results for "${search}"` : "Create your first post using the Post tab"}
            </p>
          </div>
        )}

        {/* ── GRID ── */}
        {filtered.length > 0 && (
          <div className="ap-grid">
            {filtered.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity:0, y:16 }}
                animate={{ opacity:1, y:0 }}
                transition={{ delay: i * 0.04, duration:0.35 }}
                style={S.card}
                className="ap-card"
              >
                {/* Image */}
                <div
                  style={S.imgWrap}
                  onClick={() => navigate(`/editorial/posts/edit/${post.id}`)}
                  title="Edit post"
                >
                  <img
                    src={post.featuredImage || "/default-image.jpg"}
                    alt={post.title || "Cover"}
                    style={S.img}
                    className="ap-img"
                  />
                  {/* Category badge */}
                  {post.category && (
                    <div style={S.badge}>{post.category}</div>
                  )}
                  {/* Edit overlay on hover */}
                  <div style={S.imgOverlay} className="ap-overlay">
                    <Pencil size={16} color="#fff" strokeWidth={1.5}/>
                    <span style={{ fontSize:10, color:"rgba(255,255,255,0.85)", letterSpacing:"0.14em", marginTop:4 }}>EDIT</span>
                  </div>
                </div>

                {/* Content */}
                <div style={S.cardBody}>
                  {/* Thin rule */}
                  <div style={{ width:24, height:1.5, background:C.ink, marginBottom:10 }}/>

                  <h3 style={S.postTitle} title={post.title}>
                    {post.title || "(no title)"}
                  </h3>

                  {post.description && (
                    <p style={S.postDesc}>{post.description.slice(0,80)}{post.description.length>80?"…":""}</p>
                  )}

                  {/* Actions */}
                  <div style={S.actions}>
                    <button
                      onClick={() => navigate(`/edit-post/${post.id}`)}
                      style={S.actionBtn}
                      className="ap-action"
                      title="Edit"
                    >
                      <Pencil size={11}/>
                      Edit
                    </button>

                    <button
                      onClick={() => handleDuplicate(post)}
                      disabled={dupId === post.id}
                      style={{ ...S.actionBtn, opacity: dupId===post.id ? 0.5 : 1 }}
                      className="ap-action"
                      title="Duplicate"
                    >
                      <Copy size={11}/>
                      {dupId === post.id ? "…" : "Duplicate"}
                    </button>

                    {/* Delete — two-step confirm */}
                    {deleteId === post.id ? (
                      <div style={S.deleteConfirm}>
                        <span style={{ fontSize:9, color:C.danger, fontWeight:600 }}>Delete?</span>
                        <button onClick={() => handleDelete(post.id)} style={S.deleteYes}>Yes</button>
                        <button onClick={() => setDeleteId(null)} style={S.deleteNo}>No</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteId(post.id)}
                        style={{ ...S.actionBtn, color:C.danger }}
                        className="ap-action"
                        title="Delete"
                      >
                        <Trash2 size={11}/>
                        Trash
                      </button>
                    )}

                    <button
                      onClick={() => window.open(`/${post.slug}`, "_blank")}
                      style={S.actionBtn}
                      className="ap-action"
                      title="View live"
                    >
                      <ExternalLink size={11}/>
                      View
                    </button>

                    <button
                      onClick={() => handleCopy(post.slug, post.id)}
                      style={{ ...S.actionBtn, color: copiedId===post.id ? C.success : C.inkMid }}
                      className="ap-action"
                      title="Copy link"
                    >
                      <Link size={11}/>
                      {copiedId === post.id ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllPosts;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  titleStrip: { padding:"20px 20px 0" },
  eye:        { fontSize:9, letterSpacing:"0.3em", color:C.inkMid, fontFamily:C.sans, marginBottom:6 },
  title:      { fontFamily:C.serif, fontSize:"clamp(26px,4vw,42px)" as any, fontWeight:600, color:C.ink, lineHeight:1, margin:0, letterSpacing:"-0.01em" },
  countPill:  { fontSize:10, letterSpacing:"0.14em", color:C.inkMid, fontFamily:C.sans, paddingBottom:4 },

  body: { maxWidth:1160, margin:"0 auto", padding:"24px 20px 60px" },

  /* Toolbar */
  toolbar: { display:"flex", alignItems:"center", gap:10, marginBottom:24 },
  searchWrap: {
    display:"flex", alignItems:"center", gap:8,
    background:C.surface, padding:"8px 14px",
    flex:1, maxWidth:320, transition:"border-color 0.2s",
  },
  searchInput: {
    flex:1, background:"transparent", border:"none", outline:"none",
    fontSize:12, color:C.ink, fontFamily:C.sans,
  },
  refreshBtn: {
    background:"transparent", border:`1px solid ${C.border}`,
    width:36, height:36, display:"flex", alignItems:"center",
    justifyContent:"center", cursor:"pointer", color:C.inkMid,
    flexShrink:0, transition:"border-color 0.18s, color 0.18s",
  },

  empty: { textAlign:"center" as const, padding:"64px 20px", display:"flex", flexDirection:"column", alignItems:"center" },

  /* Card */
  card: {
    background:C.paper, border:`1px solid ${C.border}`,
    overflow:"hidden", transition:"box-shadow 0.25s, transform 0.25s",
    display:"flex", flexDirection:"column",
  },
  imgWrap: {
    position:"relative", height:180, overflow:"hidden", cursor:"pointer",
    background:C.surface, flexShrink:0,
  },
  img: {
    width:"100%", height:"100%", objectFit:"cover",
    display:"block", transition:"transform 0.45s ease",
  },
  badge: {
    position:"absolute", top:10, left:10,
    background:C.ink, color:"#fff",
    fontSize:8, fontWeight:700, letterSpacing:"0.2em",
    padding:"3px 8px", fontFamily:C.sans,
    textTransform:"uppercase" as const,
  },
  imgOverlay: {
    position:"absolute", inset:0,
    background:"rgba(0,0,0,0.45)",
    display:"flex", flexDirection:"column",
    alignItems:"center", justifyContent:"center",
    opacity:0, transition:"opacity 0.25s",
  },

  cardBody: { padding:"16px 16px 14px", flex:1, display:"flex", flexDirection:"column" },
  postTitle: {
    fontFamily:C.serif, fontSize:16, fontWeight:600,
    color:C.ink, lineHeight:1.25, marginBottom:6,
    overflow:"hidden", display:"-webkit-box",
    WebkitLineClamp:2, WebkitBoxOrient:"vertical" as const,
  },
  postDesc: {
    fontSize:11, color:C.inkLight, lineHeight:1.65,
    fontFamily:C.sans, fontWeight:300, marginBottom:12, flex:1,
  },

  /* Actions */
  actions: {
    display:"flex", flexWrap:"wrap", gap:4,
    paddingTop:10, borderTop:`1px solid ${C.border}`,
    marginTop:"auto",
  },
  actionBtn: {
    display:"inline-flex", alignItems:"center", gap:4,
    background:"transparent", border:`1px solid ${C.border}`,
    color:C.inkMid, padding:"5px 10px",
    fontSize:9, fontWeight:500, letterSpacing:"0.1em",
    cursor:"pointer", fontFamily:C.sans,
    transition:"border-color 0.15s, color 0.15s",
  },

  /* Delete confirm inline */
  deleteConfirm: {
    display:"inline-flex", alignItems:"center", gap:5,
    background:C.dangerBg, border:`1px solid ${C.dangerBorder}`,
    padding:"4px 8px",
  },
  deleteYes: {
    background:C.danger, color:"#fff", border:"none",
    padding:"3px 8px", fontSize:9, fontWeight:700,
    cursor:"pointer", fontFamily:C.sans,
  },
  deleteNo: {
    background:"transparent", border:`1px solid ${C.border}`,
    color:C.inkMid, padding:"3px 8px",
    fontSize:9, cursor:"pointer", fontFamily:C.sans,
  },
};

/* ─────────────────────────────────────────────────────────
   CSS
───────────────────────────────────────────────────────── */
const AP_CSS = `
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; }

  .ap-input:focus    { outline: none; }
  input::placeholder { color: #a3a3a3; font-size: 12px; }

  .ap-card:hover { box-shadow: 0 6px 28px rgba(0,0,0,0.09); transform: translateY(-2px); }
  .ap-card:hover .ap-img { transform: scale(1.04); }
  .ap-card:hover .ap-overlay { opacity: 1 !important; }
  .ap-action:hover { border-color: #0a0a0a !important; color: #0a0a0a !important; }

  .ap-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }

  @media (max-width: 480px) {
    .ap-grid { grid-template-columns: 1fr !important; }
  }
`;